import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { GLASS_PANEL } from "./styles.ts";
import {
  COLLAPSE_MS,
  COLOR_TEXT_MUTED,
  FONT_SIZE_PANEL_TITLE,
  useChromeDim,
} from "./tokens.ts";

// Derive glass values from GLASS_PANEL — longhand only to avoid
// React shorthand/longhand conflicts with per-side border radii.
export const PANEL_SURFACE_BG = GLASS_PANEL.background;
export const PANEL_SURFACE_BLUR = GLASS_PANEL.backdropFilter as string;
export const PANEL_SURFACE_BORDER = GLASS_PANEL.border;
export const PANEL_SURFACE_RADIUS = GLASS_PANEL.borderRadius;
export const PANEL_SURFACE_HEADER_HEIGHT = 44;

export function mergeTransition(
  base: string | undefined,
  extra: string | undefined,
): string | undefined {
  if (base && extra) return `${base}, ${extra}`;
  return base ?? extra;
}

export function getPanelBodyStyle({
  padding = "10px 14px",
  overflowY,
  maxWidth,
}: {
  padding?: CSSProperties["padding"];
  overflowY?: CSSProperties["overflowY"];
  maxWidth?: CSSProperties["maxWidth"];
} = {}): CSSProperties {
  return {
    background: PANEL_SURFACE_BG,
    backdropFilter: PANEL_SURFACE_BLUR,
    WebkitBackdropFilter: PANEL_SURFACE_BLUR,
    borderTop: "none",
    borderRight: PANEL_SURFACE_BORDER,
    borderBottom: PANEL_SURFACE_BORDER,
    borderLeft: PANEL_SURFACE_BORDER,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: PANEL_SURFACE_RADIUS,
    borderBottomRightRadius: PANEL_SURFACE_RADIUS,
    padding,
    ...(overflowY ? { overflowY } : {}),
    ...(maxWidth ? { maxWidth } : {}),
  };
}

export const PanelSurfaceFrame = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    dimChrome?: boolean;
  }
>(function PanelSurfaceFrame(
  { style, dimChrome = true, children, ...props },
  ref,
) {
  const chromeDim = useChromeDim("box-shadow 150ms ease");
  return (
    <div
      ref={ref}
      {...props}
      style={{
        ...GLASS_PANEL,
        ...style,
        ...(dimChrome ? chromeDim : {}),
      }}
    >
      {children}
    </div>
  );
});

export const PanelSurfaceHeader = forwardRef<
  HTMLDivElement,
  {
    tint?: string;
    collapsed: boolean;
    pulsing: boolean;
    style?: CSSProperties;
    children: ReactNode;
  }
>(function PanelSurfaceHeader(
  { tint, collapsed, pulsing, style, children },
  ref,
) {
  const chromeDim = useChromeDim();
  const baseTransition = pulsing
    ? "none"
    : collapsed
      ? "box-shadow 400ms ease-out"
      : `border-radius ${COLLAPSE_MS}ms ease, box-shadow 400ms ease-out`;

  return (
    <div
      ref={ref}
      data-drag-handle
      style={{
        background: tint || PANEL_SURFACE_BG,
        backdropFilter: PANEL_SURFACE_BLUR,
        WebkitBackdropFilter: PANEL_SURFACE_BLUR,
        borderTop: PANEL_SURFACE_BORDER,
        borderRight: PANEL_SURFACE_BORDER,
        borderLeft: PANEL_SURFACE_BORDER,
        borderBottom: collapsed ? PANEL_SURFACE_BORDER : "none",
        borderTopLeftRadius: PANEL_SURFACE_RADIUS,
        borderTopRightRadius: PANEL_SURFACE_RADIUS,
        borderBottomLeftRadius: collapsed ? PANEL_SURFACE_RADIUS : 0,
        borderBottomRightRadius: collapsed ? PANEL_SURFACE_RADIUS : 0,
        minHeight: PANEL_SURFACE_HEADER_HEIGHT,
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: FONT_SIZE_PANEL_TITLE,
        fontWeight: 500,
        color: COLOR_TEXT_MUTED,
        cursor: "grab",
        userSelect: "none",
        touchAction: "none",
        overflow: "hidden",
        boxSizing: "border-box",
        whiteSpace: collapsed ? "nowrap" : undefined,
        boxShadow: pulsing
          ? "0 0 0 1px rgba(255,255,255,0.45), 0 0 12px 2px rgba(255,255,255,0.15)"
          : "none",
        opacity: chromeDim.opacity,
        transition: mergeTransition(
          baseTransition,
          chromeDim.transition as string | undefined,
        ),
        ...style,
      }}
    >
      {children}
    </div>
  );
});

export function PanelSurfaceBody({
  style,
  children,
}: {
  style: CSSProperties;
  children: ReactNode;
}) {
  const chromeDim = useChromeDim();
  return (
    <div
      style={{
        ...style,
        ...chromeDim,
        transition: mergeTransition(
          style.transition as string | undefined,
          chromeDim.transition as string | undefined,
        ),
      }}
    >
      {children}
    </div>
  );
}
